<template>
  <div class="feedback-tab" @click="displayFeedback" :class="{ active: displayFeedbackForm }">
    <p>Feedback</p>
  </div>
  <div class="feedback-form" :class="{ active: displayFeedbackForm, submitted: submitted }">
    <button type="button" class="close-btn" @click="closeFeedback">X</button>
    <p class="title">{{ title }}</p>
    <template v-if="submitted">
      <p>
        We appreciate your input about your experience on our site. This information will be used to help us improve.
      </p>
    </template>
    <template v-else>
      <StarRating
        :rating="rating"
        :hoverRating="hoverRating"
        @rating="setRating"
        @hover-rating="setHoverRating"
        @mouseleave="hoverRating = 0"
      />
      <div class="reason-for-contact">
        <p>Have additional feedback?</p>
        <template v-for="(option, index) in options" :key="index">
          <div class="custom-control custom-radio custom-control-inline">
            <input
              type="radio"
              :id="option?.category?.toLowerCase()"
              :name="option?.category?.toLowerCase()"
              :value="option?.id"
              class="custom-control-input"
              v-model="categoryId"
            />
            <label class="custom-control-label" :for="option?.category?.toLowerCase()">
              {{ option?.category }}
            </label>
          </div>
        </template>
        <div v-show="categoryId > 0" class="form-group contact-reason">
          <textarea
            class="form-control"
            id="contactReason"
            v-model="message"
            placeholder="Share your experience but refrain from including any sensitive personal information such as account numbers or usernames."
            rows="4"
            maxlength="800"
          ></textarea>
        </div>
      </div>
      <div v-show="errorMessage !== ''" class="error-message">
        {{ errorMessage }}
      </div>
      <button type="button" class="btn feedback-btn" :disabled="rating <= 0 || sending" @click.prevent="submitFeedback">
        {{ sending ? "Sending..." : "Send Feedback" }}
      </button>
      <p class="disclosure">
        Thank you for your feedback! If you require a response, please click
        <span class="underline" @click="emit('showSupportModal')">here</span> for support options.
      </p>
    </template>
  </div>
</template>
<script setup lang="ts">
import { inject, ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { $api } from "@/services/api";
import StarRating from "@/components/shared/feedback/StarRating.vue";
import type { PostFeedbackRequest, FeedbackRequestOption } from "@/models/feedback";

const store = useStore();
const gtag = inject<any>("gtag");
const title = ref<string>("How would you rate your experience?");
const displayFeedbackForm = ref<boolean>(false);
const rating = ref<number>(0);
const hoverRating = ref<number>(0);
const categoryId = ref<number>(0);
const message = ref<string>("");
const errorMessage = ref<string>("");
const sending = ref<boolean>(false);
const submitted = ref<boolean>(false);
const options = computed(() => store.getters["feedbackFormOptions"]);
const lenderId = computed(() => store.state?.appData?.application?.lenderId);
const applicationId = computed(() => store.state?.appData?.application?.id);
const applicantId = computed(() => {
  if (store.state?.appData?.applicants && store.state?.appData?.applicants?.length > 0) {
    return store.state?.appData?.applicants[store.state?.appData?.applicants?.length - 1]?.id;
  } else {
    return null;
  }
});
const defaultOptionId = computed(
  () => options.value?.find((o: FeedbackRequestOption) => o?.category === "General")?.id,
);
const emit = defineEmits(["showSupportModal"]);

function reset() {
  title.value = "How would you rate your experience?";
  submitted.value = false;
  rating.value = 0;
  hoverRating.value = 0;
  categoryId.value = 0;
  message.value = "";
  errorMessage.value = "";
}

function closeFeedback() {
  displayFeedbackForm.value = false;
  setTimeout(() => {
    reset();
  }, 500);
}

function setHoverRating(event: any) {
  hoverRating.value = event.hoverRating;
}

function setRating(event: any) {
  rating.value = event.rating;
}

function handleError() {
  sending.value = false;
  errorMessage.value = "Feedback could not be submitted.";
}

function displayFeedback() {
  displayFeedbackForm.value = true;
  gtag.event("feedback_opened", {
    event_category: "engagement",
    event_label: "Feedback Opened",
  });
}

function submitFeedback() {
  sending.value = true;
  errorMessage.value = "";

  $api.feedback
    .sendFeedback({
      formId: 2, // Borrower App Initiated
      lenderId: lenderId.value,
      categoryId: categoryId.value <= 0 ? defaultOptionId.value : categoryId.value, // default to general if they don't select
      rating: rating.value,
      message: message.value,
      metadata: {
        applicationId: applicationId.value,
        applicantId: applicantId.value,
      },
    } as PostFeedbackRequest)
    .then((result: any) => {
      if (result.error) {
        handleError();
      } else {
        title.value = "Thank you for sharing your feedback with us!";
        sending.value = false;
        submitted.value = true;
      }
    })
    .catch((error: any) => {
      console.log("ERROR", error);
      handleError();
    });
}

onMounted(() => {
  store.dispatch("getFeedbackFormOptions");
});
</script>
<style scoped lang="scss">
.feedback-tab {
  position: fixed;
  top: 21rem;
  right: -3.5rem;
  transform: rotate(270deg);
  z-index: 1001;
  color: #fff;
  background: #4a4444;
  padding: 0px 30px;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  transition: right 0.25s;
}

.feedback-tab:focus,
.feedback-tab:hover {
  cursor: pointer;
  right: -3rem;
  transition: right 0.25s;
  scale: 1;
}

.feedback-form {
  position: fixed;
  top: 20%;
  right: -28rem;
  min-height: 300px;
  width: 375px;
  max-width: 90%;
  background: #fff;
  border-radius: 0.438rem;
  -webkit-box-shadow: 0px 0px 15px 5px rgba(0, 0, 0, 0.5);
  box-shadow: 0px 0px 15px 5px rgba(0, 0, 0, 0.5);
  transition: right 0.5s;
}

.feedback-form.active {
  right: 3rem;
  transition: right 0.5s;
  z-index: 1001;
  padding: 1.5625rem 1.5rem 0;
}

.feedback-form.submitted {
  font-size: 0.9rem;
  min-height: 100px;
  height: auto;
  transition: right 0.5s;
}

.feedback-form .close-btn {
  -webkit-appearance: none;
  -moz-appearance: none;
  position: absolute;
  top: 0;
  right: 0;
  color: #fff;
  background: #4a4444;
  font-weight: 700;
  padding: 10px 15px;
  border-top-right-radius: 0.438rem;
  border-bottom-left-radius: 0.438rem;
  outline: none;
  border: none;
  cursor: pointer;
}

.feedback-form .close-btn:active {
  transform: scale(0.98);
}

.feedback-form .close-btn:focus,
.feedback-form .close-btn:active {
  outline: none;
}

.feedback-form .title {
  text-align: center;
  font-size: 1.275rem;
  font-weight: 600;
}

.reason-for-contact {
  margin-top: 1rem;
  text-align: center;
}

.reason-for-contact p {
  font-weight: 600;
  margin-bottom: 0;
}

.reason-for-contact label {
  font-size: 0.9rem;
}

.reason-for-contact .contact-reason {
  margin-top: 0.5rem;
  /* max-width: 100%; */
}
.reason-for-contact textarea {
  width: 95%;
  max-width: 95%;
  min-height: 100px;
  max-height: 200px;
}

.feedback-form .feedback-btn {
  color: #fff;
  background: #4a4444;
  border-radius: 0.438rem;
  width: 100%;
  font-weight: 600;
  padding: 10px 0;
  margin-top: 1rem;
}

.feedback-form .error-message {
  color: red;
  border: 1px solid red;
  border-radius: 0.438rem;
  padding: 10px 0;
  text-align: center;
  margin-top: 1rem;
}

.disclosure {
  font-size: 0.75rem;
  margin-bottom: 0;
  margin-top: 1rem;
}

.disclosure a,
.disclosure span {
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 991px) {
  .feedback-tab {
    font-size: 0.9rem;
    padding: 8px 30px;
  }

  .feedback-form.active {
    text-align: center;
  }

  .btn {
    max-width: 100%;
  }
}

@media (max-width: 600px) {
  .feedback-form {
    top: 3%;
  }

  .feedback-form.active {
    right: 1rem;
    z-index: 2001;
    text-align: center;
    padding: 0;
  }

  .feedback-form .title {
    margin-top: 3rem;
    margin-bottom: 0.5rem;
  }

  .reason-for-contact textarea {
    max-width: -webkit-fill-available;
  }

  .feedback-form.active .feedback-btn {
    max-width: calc(100% - 25px);
  }
}

@media (max-width: 350px) {
  .custom-control.custom-radio.custom-control-inline {
    display: block;
  }
}
</style>
